import { z } from "zod";

export const CrossBorderCountriesSchema = z.enum(["NL", "DE"]);
export type CrossBorderCountries = z.infer<typeof CrossBorderCountriesSchema>;

/**
 * A region-discriminated price for a cross-border SKU. One entry per ship-to
 * country: the roaster's home region carries the prices they entered, every
 * other region carries the auto-derived (tax-adjusted) prices. Persisted in the
 * product target metadata and carried on price-update approval requests so the
 * backend can route each region's price to its channel(s).
 */
export const SkuRegionPriceSchema = z.object({
  region: CrossBorderCountriesSchema,
  msrp: z.number(),
  wholesalePrice: z.number(),
  // The bruto wholesale price shown in this region's UI wholesale field — the
  // raw as-typed value for the home region, the derived gross total for every
  // other region — set on every entry when the roaster's home region is DE,
  // since only a DE-home roaster's Kaffeesteuer needs reconciling against what
  // was shown/entered. Absent entirely for non-DE-home roasters.
  roasterWholesalePrice: z.number().optional(),
});
export type SkuRegionPrice = z.infer<typeof SkuRegionPriceSchema>;

/**
 * All countries a vendor can ship to, including their own region. Same value set as
 * CountryCodeSchema (the vendor's own resolved country) — kept as a separate export since
 * "which countries can I ship to" and "what country am I in" are distinct concepts that may
 * diverge later, even though today's allowed values match.
 */
export { CountryCodeSchema as VendorShipToCountriesSchema } from "./countryCode.enum";
export type { VendorCountryCode as VendorShipToCountries } from "./countryCode.enum";
